/**
 * @fileoverview ReportEditor Component - Form for editing incident reports
 * Handles the actual form fields and auto-save functionality for incident reports.
 * 
 * @author SmarterServices Platform Team
 * @version 1.0.0
 * @since 2025-10-21
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Flex, TextArea, TextInput, View, Pill, IconCheckLine, Button, IconTrashLine, Text } from '@smarterservices/smarter-ui';
import {
  useExamSessionStore,
  getStore,
} from '../../../stores/StoreContext';
import { NavigationalToolTip } from '@ui/shared/components/Common/NavigationalToolTip';
import ButtonWithConfirmation from '@ui/shared/components/Common/ButtonWithConfirmation';
import { useHotkeys } from 'react-hotkeys-hook';
import CONSTANTS from '../../../config/constants';

/**
 * ReportEditor Component
 * 
 * Handles the editing interface for incident reports with:
 * - Real-time auto-save functionality (5-second intervals)
 * - Blur-to-save for immediate persistence
 * - Draft and submitted status management
 * - Keyboard shortcuts for quick actions
 * - Full CRUD operations (Create, Read, Update, Delete)
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.initialData - Initial data for the report (subject, details)
 * @param {Function} props.onSubmitted - Callback when report is successfully submitted
 * @returns {JSX.Element} The rendered report editor
 */
function ReportEditor({ initialData = {}, onSubmitted }) {
  const incidentReport = useExamSessionStore((state) => state.incidentReport);
  const ExamSessionStore = getStore('examSessionStore');
  const AppStore = getStore('appStore');

  // Local form state
  const [subject, setSubject] = useState(initialData.subject || '');
  const [details, setDetails] = useState(initialData.details || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Timer reference for auto-save functionality
   * @type {React.MutableRefObject<NodeJS.Timeout|null>}
   */
  const autoSaveTimerRef = useRef(null);

  /**
   * Tracks the last saved values to prevent race conditions during auto-save
   * @type {React.MutableRefObject<{subject: string, details: string}>}
   */
  const savedValuesRef = useRef({ subject: '', details: '' });

  // Initialize form fields when incidentReport data loads
  useEffect(() => {
    if (incidentReport) {
      const newSubject = incidentReport.subject || '';
      const newDetails = incidentReport.details || '';

      // Only update form fields if this is the initial load or if the data has changed from server
      // Don't update if user is actively editing (has unsaved changes)
      if (!hasChanges) {
        if (subject !== newSubject) {
          setSubject(newSubject);
          savedValuesRef.current.subject = newSubject;
        }

        if (details !== newDetails) {
          setDetails(newDetails);
          savedValuesRef.current.details = newDetails;
        }
      }
    }
  }, [incidentReport, hasChanges, subject, details]);

  // Update form when initialData changes (from AI generation)
  useEffect(() => {
    if (initialData.subject !== undefined) {
      setSubject(initialData.subject);
      setHasChanges(true);
    }
    if (initialData.details !== undefined) {
      setDetails(initialData.details);
      setHasChanges(true);
    }
  }, [initialData]);

  /**
   * Submits the incident report by saving it with draft: false status
   */
  const handleSubmit = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    // Save the values we're submitting
    const currentSubject = subject;
    const currentDetails = details;
    savedValuesRef.current = { subject: currentSubject, details: currentDetails };

    try {
      const params = {
        subject: currentSubject,
        details: currentDetails,
        draft: false, // Mark as submitted
        installSid: ExamSessionStore.installSid,
        examSid: ExamSessionStore.examSid,
        sessionSid: ExamSessionStore.sessionSid,
      };

      let result;
      if (incidentReport?.sid) {
        // Update existing incident report
        result = await AppStore.ApiClient.updateIncidentReport({
          ...params,
          eventReportSid: incidentReport.sid,
        });
      } else {
        // Create new incident report
        result = await AppStore.ApiClient.createIncidentReport(params);
      }

      if (result.successful) {
        // Update the store with the returned data instead of refetching
        if (result.data) {
          ExamSessionStore.setIncidentReport(result.data);
        }
        setHasChanges(false);

        // Call the onSubmitted callback to show success view
        if (onSubmitted) {
          onSubmitted();
        }
      } else {
        console.error('Failed to submit incident report:', result.error);
      }
    } catch (error) {
      console.error('Error submitting incident report:', error);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, subject, details, incidentReport, ExamSessionStore, AppStore, onSubmitted]);

  /**
   * Permanently deletes the incident report from the system
   */
  const handleDelete = useCallback(async () => {
    if (!incidentReport?.sid || isSaving) return;

    setIsSaving(true);

    try {
      const result = await AppStore.ApiClient.deleteIncidentReport({
        installSid: ExamSessionStore.installSid,
        examSid: ExamSessionStore.examSid,
        sessionSid: ExamSessionStore.sessionSid,
        eventReportSid: incidentReport.sid,
      });

      if (result.successful) {
        // Clear the incident report from the store
        ExamSessionStore.setIncidentReport(null);
        // Reset form state
        setSubject('');
        setDetails('');
        setHasChanges(false);
      } else {
        console.error('Failed to delete incident report:', result.error);
      }
    } catch (error) {
      console.error('Error deleting incident report:', error);
    } finally {
      setIsSaving(false);
    }
  }, [incidentReport, isSaving, ExamSessionStore, AppStore]);

  // Revert to draft function - changes submitted report back to draft
  const handleRevertToDraft = useCallback(async () => {
    if (!incidentReport?.sid || isSaving) return;

    setIsSaving(true);

    try {
      const params = {
        subject,
        details,
        draft: true, // Revert to draft status
        installSid: ExamSessionStore.installSid,
        examSid: ExamSessionStore.examSid,
        sessionSid: ExamSessionStore.sessionSid,
        eventReportSid: incidentReport.sid,
      };

      const result = await AppStore.ApiClient.updateIncidentReport(params);

      if (result.successful) {
        // Update the store with the returned data
        if (result.data) {
          ExamSessionStore.setIncidentReport(result.data);
        }
        setHasChanges(false);
      } else {
        console.error('Failed to revert incident report to draft:', result.error);
      }
    } catch (error) {
      console.error('Error reverting incident report to draft:', error);
    } finally {
      setIsSaving(false);
    }
  }, [incidentReport, isSaving, subject, details, ExamSessionStore, AppStore]);

  /**
   * Auto-save function that saves the incident report as a draft
   */
  const saveIncidentReport = useCallback(async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);

    // Save the values we're submitting
    const currentSubject = subject;
    const currentDetails = details;
    savedValuesRef.current = { subject: currentSubject, details: currentDetails };

    try {
      const params = {
        subject: currentSubject,
        details: currentDetails,
        draft: incidentReport?.draft !== false, // Keep as draft unless explicitly submitted
        installSid: ExamSessionStore.installSid,
        examSid: ExamSessionStore.examSid,
        sessionSid: ExamSessionStore.sessionSid,
      };

      let result;
      if (incidentReport?.sid) {
        // Update existing incident report
        result = await AppStore.ApiClient.updateIncidentReport({
          ...params,
          eventReportSid: incidentReport.sid,
        });
      } else {
        // Create new incident report
        result = await AppStore.ApiClient.createIncidentReport(params);
      }

      if (result.successful) {
        // Update the store with the returned data instead of refetching
        if (result.data) {
          ExamSessionStore.setIncidentReport(result.data);
        }
        setHasChanges(false);
      } else {
        console.error('Failed to save incident report:', result.error);
      }
    } catch (error) {
      console.error('Error saving incident report:', error);
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, isSaving, subject, details, incidentReport, ExamSessionStore, AppStore]);

  // Auto-save effect - start timer only if none exists
  // the problem with this is it does not work well when you are still typing...commenting out for now until it can be fixed...
  {/*
  useEffect(() => {
    if (hasChanges && !autoSaveTimerRef.current && !isSaving) {
      console.log('Starting auto-save timer');
      
      autoSaveTimerRef.current = setTimeout(() => {
        console.log('Auto-save timer fired');
        // Double-check that we still have changes before saving
        if (hasChanges && !isSaving) {
          saveIncidentReport();
        }
        autoSaveTimerRef.current = null; // Clear ref after save
      }, 5000);
    }
  }, [hasChanges, isSaving, saveIncidentReport]);
  */}

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, []);

  // Keyboard shortcut for submit (Ctrl+Shift+S)
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.INCIDENT_REPORT.OPTIONS.SUBMIT.KEY,
    (event) => {
      event.preventDefault();
      // Only submit if there's a draft incident report and we're not already saving
      if (incidentReport?.draft !== false) {
        handleSubmit();
      }
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true
    }
  );

  // Handle subject change
  const handleSubjectChange = useCallback((event, value) => {
    setSubject(value);
    setHasChanges(true);
  }, []);

  // Handle details change
  const handleDetailsChange = useCallback((event) => {
    const newValue = event?.target?.value || '';
    setDetails(newValue);
    setHasChanges(true);
  }, []);

  // Handle field blur - trigger immediate save
  const handleFieldBlur = useCallback(() => {
    if (hasChanges && !isSaving) {
      // Clear the auto-save timer since we're saving immediately
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      saveIncidentReport();
    }
  }, [hasChanges, isSaving, saveIncidentReport]);

  return (
    <View as="div" position="relative">
      {incidentReport?.draft && (
        <View as="div"
          className="auto-scroll-toggle"
          background="primary"
        >
          <View
            as="div"
            background="secondary"
            borderColor="primary"
            borderRadius="medium"
            borderWidth="small"
            padding="x-small"
            margin="none none xx-small none"
          >
            <Flex justifyItems="space-between" alignItems="center">
              <Flex.Item>
                {incidentReport?.draft && (
                  <Pill
                    statusLabel="Status"
                    color="info"
                  >
                    Draft
                  </Pill>
                )}
                {(incidentReport?.draft === false) && (
                  <Pill
                    statusLabel="Status"
                    renderIcon={<IconCheckLine />}
                    color="success"
                  >
                    Submitted
                  </Pill>
                )}
                {!incidentReport && (
                  <Pill statusLabel="Status">
                    New
                  </Pill>
                )}

              </Flex.Item>
              <Flex.Item>
                {isSaving && (
                  <Pill color="info">
                    Saving...
                  </Pill>
                )}
                {hasChanges && !isSaving && (
                  <Pill color="warning">
                    Unsaved Changes
                  </Pill>
                )}


                {(!hasChanges && !isSaving && incidentReport?.draft) && (
                  <NavigationalToolTip
                    description="Submit Incident Report"
                    placement="bottom end"
                    shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.INCIDENT_REPORT?.OPTIONS?.SUBMIT?.KEY}
                  >
                    <Button
                      color="success"
                      size="small"
                      onClick={handleSubmit}
                      disabled={isSaving}
                    >
                      Submit
                    </Button>
                  </NavigationalToolTip>
                )}

                {(incidentReport && incidentReport?.draft === false && !isSaving) && (
                  <Button
                    color="primary"
                    size="small"
                    onClick={handleRevertToDraft}
                    disabled={isSaving}
                  >
                    Revert To Draft
                  </Button>
                )}
                {(incidentReport && !isSaving) && (
                  <ButtonWithConfirmation
                    margin="none none none x-small"
                    onConfirm={handleDelete}
                    confirmationText="Are you sure?"
                    confirmButtonText="Yes"
                    confirmButtonColor="danger"
                    color="danger"
                    screenReaderLabel="Delete this incident report?"
                  >
                    <IconTrashLine />
                  </ButtonWithConfirmation>
                )}
              </Flex.Item>
            </Flex>
          </View>
        </View>
      )}
      <Flex as="div" direction="column">
        <Flex.Item padding="small">
          {(incidentReport?.draft === false) ? (
            // Read-only view for submitted reports
            <View as="div">
              <Text as="p" size="medium" weight="bold" display="block" margin="0 0 x-small 0">
                Subject
              </Text>
              <Text size="medium" display="block">
                {subject || 'No subject provided'}
              </Text>
            </View>
          ) : (
            // Editable input for draft reports
            <TextInput
              renderLabel="Subject"
              placeholder="Enter a subject/summary for this report"
              value={subject}
              onChange={handleSubjectChange}
              onBlur={handleFieldBlur}
            />
          )}
        </Flex.Item>
        <Flex.Item padding="small">
          {(incidentReport?.draft === false) ? (
            // Read-only view for submitted reports
            <View as="div">
              <Text as="p" size="medium" weight="bold" display="block" margin="0 0 x-small 0">
                Details
              </Text>
              <Text size="medium" display="block" style={{ whiteSpace: 'pre-wrap' }}>
                {details || 'No details provided'}
              </Text>
            </View>
          ) : (
            // Editable textarea for draft reports
            <TextArea
              renderLabel="Details"
              label="Details"
              placeholder="Enter a description of the incident"
              value={details}
              onChange={handleDetailsChange}
              onBlur={handleFieldBlur}
            />
          )}
        </Flex.Item>
      </Flex>
    </View>
  );
}

export default ReportEditor;
